import { MainButton } from "@/Components/MainButton"
import { getUserCreds, socket } from "@/utils/utils"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  setModalData,
  setState,
  setActiveTrade,
  setTradeData,
} from "@/store/globalSlice"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { maxTurnsToBeBankrupt } from "@mono/constants"
import classNames from "classnames"
import playCardsIcon from "@/images/icons/cards.png"
import Modal from "@/Components/Modal"
import GameTimer from "@/Components/GameTimer"
import { useGameTimings } from "@/Hooks/useGameTimings"

interface JailModalProps {}

const BankruptModal: React.FC<JailModalProps> = () => {
  const dispatch = useAppDispatch()
  const { turnTotalTime } = useGameTimings()
  const myGame = useAppSelector((state) => state.global.myGame)
  const modalData = useAppSelector((state) => state.global.modalData)
  const idleWarning = useAppSelector((state) => state.global.idleWarning)

  if (!myGame?.situation) return null
  const myOrder = myGame.situation.players.find(
    (player) => player.id === getUserCreds().email
  )?.order as PlayerOrder
  const activePlayer = myGame.situation.players.find(
    (player) => player.id === myGame?.situation?.turn
  )
  if (activePlayer?.order !== myOrder) return null

  const currentPlayer = myGame.situation.players.find(
    (player) => player.id === getUserCreds().email
  )
  // `turnsBeingBankrupt` counts the turns already *ended* on a negative balance.
  // The server bankrupts you once ending a turn pushes it to `maxTurnsToBeBankrupt`,
  // so the turns left below include the one being played right now — at 1 this is
  // the last turn, and ending it still negative removes you. It goes back to a
  // full countdown the moment the balance is non-negative again, whoever's turn
  // it is when that happens, so this can jump back to 3 mid-turn.
  const negativeTurnsSpent = currentPlayer?.turnsBeingBankrupt || 0
  const turnsLeftBeforeBankruptcy = maxTurnsToBeBankrupt - negativeTurnsSpent
  const isFinalTurn = turnsLeftBeforeBankruptcy <= 1
  const debt = Math.abs(currentPlayer?.money || 0)

  // Idle auto-end countdown (useIdleAutoAction). This modal covers the board for
  // as long as the balance is negative, so the badge that normally lives under
  // the "is playing" label can't be seen from here — but the clock behind it is
  // still running and still about to spend one of the turns counted above. It is
  // repeated below rather than moved, since the rest of the table watches the
  // board copy. Never appears on the final turn: the hook stops tracking there,
  // for the same reason the End turn button is gone.
  const autoEndSecondsLeft =
    idleWarning?.playerOrder === myOrder && idleWarning.action === "end-turn"
      ? idleWarning.secondsLeft
      : null
  const isAutoEndUrgent = (autoEndSecondsLeft ?? 0) <= 3

  return (
    <Modal
      data-testid="game.bankrupt-modal"
      className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-[90%] md:w-auto max-w-2xl"
      icon={<ExclamationTriangleIcon className="h-8 w-8 text-white" />}
      title="You're in Debt"
      subtitle={`Get back to M0 within ${maxTurnsToBeBankrupt} of your turns or you're out of the game`}
      contentClassName="flex flex-col gap-5 px-5 md:px-8 pb-5 md:pb-8"
    >
      {/* Turn Timer — how long is left on the current turn */}
      <div className="absolute top-4 right-4 z-20">
        <GameTimer
          timerKey="turnTimeLeft"
          totalTime={turnTotalTime}
          fallback={0}
          size="lg"
          showLabel={false}
          timeClassName="text-white"
        />
      </div>

      {/* Bankruptcy Countdown — where this turn sits in the 3-turn grace period */}
      <div
        className={classNames(
          "flex flex-col items-center gap-2.5 px-4 py-3 border rounded-2xl font-semibold",
          isFinalTurn
            ? "bg-red-500/10 border-red-400/50 text-red-200"
            : "bg-amber-500/10 border-amber-400/50 text-amber-200"
        )}
      >
        <div className="flex items-center gap-2 text-center">
          <ExclamationTriangleIcon
            className={classNames(
              "h-5 w-5 flex-shrink-0",
              isFinalTurn && "animate-pulse"
            )}
          />
          <span className="text-sm md:text-base">
            {turnsLeftBeforeBankruptcy > 1
              ? `${turnsLeftBeforeBankruptcy} turns to clear it — this one counts`
              : "Last turn — clear it now or you're out"}
          </span>
        </div>

        {/* One bar per turn: dim bars are turns already ended in the red */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: maxTurnsToBeBankrupt }).map((_, index) => {
            const isSpent = index < negativeTurnsSpent
            const isLastChance = index === maxTurnsToBeBankrupt - 1
            return (
              <span
                key={index}
                className={classNames(
                  "h-1.5 w-8 rounded-full",
                  isSpent
                    ? "bg-white/20"
                    : isLastChance
                    ? "bg-red-400"
                    : "bg-amber-300"
                )}
              />
            )
          })}
        </div>
      </div>

      {/* Main Message */}
      <div className="font-poppins text-base md:text-lg text-center font-medium text-gray-300 leading-relaxed max-w-lg mx-auto">
        You're{" "}
        <span className="text-red-300 font-semibold">
          M{debt.toLocaleString()}
        </span>{" "}
        in debt.{" "}
        {isFinalTurn
          ? "Clear it now or you're out."
          : "Clear it and the countdown resets."}
      </div>

      {/* Auto end turn countdown — see `autoEndSecondsLeft` above. Sits directly
          on top of the buttons, because touching any of them is what clears it. */}
      {autoEndSecondsLeft !== null && (
        <div
          role="status"
          data-testid="game.bankrupt-modal-auto-end-turn"
          className={classNames(
            "flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-xl border font-poppins",
            isAutoEndUrgent
              ? "bg-[#5c1616]/80 border-[#ff8a6b]/60 auto-end-turn-badge-urgent"
              : "bg-[#3d2408]/80 border-[#f0b45c]/60 auto-end-turn-badge"
          )}
        >
          <i
            className={classNames(
              "fa-hourglass-half fa-solid flex-shrink-0",
              isAutoEndUrgent ? "text-[#ff9c7d]" : "text-[#f0b45c]"
            )}
          ></i>
          <span className="text-sm md:text-base font-medium text-white/90">
            You've gone quiet — turn auto-ends in{" "}
            <span
              className={classNames(
                "font-bold tabular-nums",
                isAutoEndUrgent ? "text-[#ffb3a0]" : "text-[#ffcf7a]"
              )}
            >
              {autoEndSecondsLeft}s
            </span>
            , using up one of your {maxTurnsToBeBankrupt} turns
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <MainButton
          onClick={() => {
            dispatch(setModalData({ bankrupt: { visible: false } }))
            dispatch(setState({ houseBuildMode: "sell" }))
          }}
          data-testid="game.bankrupt-modal-buy-sell-houses-btn"
          baseColor="#b6a84b"
          size="base"
          className="w-full"
        >
          <div className="flex items-center justify-center gap-2.5">
            <i className="fa-house fa-solid text-white fa-lg w-6 text-center"></i>
            <div className="text-white font-medium font-poppins text-lg md:text-xl">
              Sell houses
            </div>
          </div>
        </MainButton>

        <MainButton
          onClick={() => {
            // Same as the board's Trade button: close any open drawer first, so
            // the half-built draft below can't leave a drawer flagged open that
            // renders nothing. That matters most here — while the trade flag is
            // up this modal stays suppressed, so a player on a negative balance
            // would be left with no drawer and no way back to these options.
            dispatch(setState({ viewedTradeId: null }))
            dispatch(setModalData({ trade: { visible: false } }))
            dispatch(
              setActiveTrade({
                givenProperties: [],
                moneyGiven: 0,
                moneyTaken: 0,
                offeringPlayerOrder: myOrder,
                offeredPlayerOrder: null as any,
                takenProperties: [],
              })
            )
            dispatch(setTradeData({ status: "createing-offer" }))
            dispatch(setState({ tempTradeData: { status: "choosing-player" } }))
          }}
          baseColor="#4f8fc9"
          data-testid="game.bankrupt-modal-trade-btn"
          size="base"
          className="w-full"
        >
          <div className="flex items-center justify-center gap-2.5">
            <i className="fa-handshake fa-solid text-white fa-lg w-6 text-center"></i>
            <div className="text-white font-medium font-poppins text-lg md:text-xl">
              Trade
            </div>
          </div>
        </MainButton>
        <MainButton
          onClick={() => {
            const myOrder = myGame?.situation?.players.find(
              (player) => player.id === getUserCreds().email
            )?.order
            if (myOrder !== undefined) {
              dispatch(
                setModalData({
                  playerCards: { visible: true, playerOrder: myOrder },
                })
              )
            }
          }}
          baseColor="#8b5cf6"
          data-testid="game.bankrupt-modal-leave-btn"
          size="base"
          // No End turn button on the final turn, so this one takes the whole row.
          className={classNames("w-full", isFinalTurn && "col-span-2")}
        >
          <div className="flex items-center justify-center gap-2.5">
            <img src={playCardsIcon} className="h-7 w-6 object-contain"></img>
            <div className="text-white font-medium font-poppins text-lg md:text-xl">
              Play Cards
            </div>
          </div>
        </MainButton>
        {/* Ending the final turn in the red is what removes you, so the button that
            does it is taken away — there is nothing left to do but clear the debt. */}
        {!isFinalTurn && (
          <MainButton
            onClick={() => {
              dispatch(setModalData({ bankrupt: { visible: false } }))
              socket.emit("game.endTurn", myGame?.id || "")
            }}
            baseColor="#d48e3d"
            data-testid="game.bankrupt-modal-leave-btn"
            size="base"
            className="w-full"
          >
            <div className="flex items-center justify-center gap-2.5">
              <i className="fa-rotate-right fa-solid text-white fa-lg w-6 text-center"></i>
              <div className="text-white font-medium font-poppins text-lg md:text-xl">
                End turn
              </div>
            </div>
          </MainButton>
        )}
      </div>

      {/* What ending the turn while still negative actually costs */}
      <div className="text-center text-xs md:text-sm text-white/50 font-poppins -mt-2">
        {isFinalTurn
          ? "You can't end this turn — when it runs out, you're bankrupt."
          : `Ending your turn while you're negative is allowed — it just uses up one of your ${maxTurnsToBeBankrupt} turns.`}
      </div>

      {/* Divider */}
      <div className="h-px w-full bg-white/10" />

      {/* Declare Bankruptcy */}
      <MainButton
        onClick={() => {
          dispatch(setState({ declaredBankruptcy: true }))
          dispatch(setModalData({ bankrupt: { visible: false } }))
          socket.emit("game.leave-game", myGame?.id || "", myOrder)
        }}
        baseColor="rgb(202, 60, 60)"
        data-testid="game.bankrupt-modal-leave-btn"
        size="lg"
        className="w-full"
      >
        <div className="flex items-center justify-center gap-2.5">
          <ExclamationTriangleIcon className="h-7 w-7 text-white" />
          <div className="text-white font-semibold font-poppins text-lg md:text-xl">
            Declare Bankruptcy
          </div>
        </div>
      </MainButton>

      {/* Warning Note */}
      <div className="flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500/10 border border-yellow-400/40 rounded-lg text-center">
        <i className="fa-triangle-exclamation fa-solid text-yellow-300 flex-shrink-0"></i>
        <div className="text-yellow-200 text-xs md:text-sm">
          Giving up now: you leave the game <strong>permanently</strong> and
          your properties are split between the remaining players. You don't
          have to — you still have{" "}
          {turnsLeftBeforeBankruptcy > 1
            ? `${turnsLeftBeforeBankruptcy} turns`
            : "this turn"}{" "}
          to turn it around.
        </div>
      </div>
    </Modal>
  )
}

export default BankruptModal
